import { EXPORT_FONT, svgToImage, roundRect, wrapText } from './svgExport';
import { formatPulledStamp } from './formatters';

// A fixed desktop-width canvas, so the picture is the same whether it was
// exported from a laptop or a phone. The on-screen page is responsive; this
// deliberately is not.
export const REPORT_IMAGE = {
  width: 1600,
  pad: 40,
  gap: 16,
  scale: 2,          // rasterise at 2x so text stays crisp
};

const CARD_W = (REPORT_IMAGE.width - REPORT_IMAGE.pad * 2 - REPORT_IMAGE.gap) / 2;
export const REPORT_CHART_W = CARD_W - 32;
// A `fullWidth` card spans both columns on a row of its own.
const FULL_CARD_W = REPORT_IMAGE.width - REPORT_IMAGE.pad * 2;
export const REPORT_FULL_CHART_W = FULL_CARD_W - 32;

const cardWidth = (metric) => (metric.fullWidth ? FULL_CARD_W : CARD_W);
const chartWidth = (metric) => (metric.fullWidth ? REPORT_FULL_CHART_W : REPORT_CHART_W);

const INK = '#1a202c';
const MUTED = '#718096';
const FAINT = '#94a3b8';
const CARD_BG = '#ffffff';
const PAGE_BG = '#f7fafc';
const BORDER = '#e2e8f0';

function shortDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y.slice(2)}`;
}

// A multi-series card's key, wrapped into rows that fit the card. Recharts
// draws no legend into the chart's own SVG, so it is painted onto the canvas.
const LEGEND_ROW_H = 18;
function planLegend(ctx, items, maxW) {
  ctx.font = `12px ${EXPORT_FONT}`;
  const rows = [];
  let row = [], x = 0;
  for (const item of items) {
    const w = 18 + ctx.measureText(item.text).width;
    if (row.length && x + w > maxW) { rows.push(row); row = []; x = 0; }
    row.push({ ...item, x, w });
    x += w + 16;
  }
  if (row.length) rows.push(row);
  return rows;
}

// Lay the cards out in the same two-column order the page uses, measuring each
// row by its taller card so a note under one chart can't overlap the next row.
// A full-width card closes any half-filled row and takes one to itself.
function planRows(metrics, chartSizes) {
  const rows = [];
  let row = [];
  for (const m of metrics) {
    const size = chartSizes.get(m.key) || { w: chartWidth(m), h: 140 };
    const card = { metric: m, chartH: size.h, noteLines: [], legendRows: [], legendH: 0 };
    if (m.fullWidth) {
      if (row.length) rows.push(row);
      rows.push([card]);
      row = [];
      continue;
    }
    row.push(card);
    if (row.length === 2) { rows.push(row); row = []; }
  }
  if (row.length) rows.push(row);
  return rows;
}

/**
 * Draw the whole Breakers Report as one PNG.
 *
 * `svgs` maps a metric key to the live recharts <svg> for that chart, taken
 * from an off-screen stage already laid out at REPORT_CHART_W.
 */
export async function composeReportImage({ svgs, metrics, tiles, range, presetLabel, title = 'Breakers Report', footnote }) {
  const { width: W, pad: PAD, gap: GAP, scale } = REPORT_IMAGE;

  // Rasterise every chart first: the layout depends on their heights.
  const chartSizes = new Map();
  const images = new Map();
  for (const m of metrics) {
    const svg = svgs.get(m.key);
    if (!svg) continue;
    const box = svg.getBoundingClientRect();
    const h = Math.round(box.height) || 140;
    chartSizes.set(m.key, { w: chartWidth(m), h });
    images.set(m.key, await svgToImage(svg, chartWidth(m), h));
  }

  // Measure text on a scratch context before sizing the real one.
  const measure = document.createElement('canvas').getContext('2d');
  const rows = planRows(metrics, chartSizes);
  measure.font = `italic 11px ${EXPORT_FONT}`;
  for (const row of rows) {
    for (const card of row) {
      const w = cardWidth(card.metric);
      card.noteLines = card.metric.note ? wrapText(measure, card.metric.note, w - 40) : [];
      card.legendRows = card.metric.legend?.length ? planLegend(measure, card.metric.legend, w - 40) : [];
      card.legendH = card.legendRows.length ? card.legendRows.length * LEGEND_ROW_H + 8 : 0;
      measure.font = `italic 11px ${EXPORT_FONT}`;
      card.height = 46 + card.legendH + card.chartH + (card.noteLines.length ? card.noteLines.length * 14 + 10 : 0) + 16;
    }
  }
  const rowHeights = rows.map(r => Math.max(...r.map(c => c.height)));

  const headerH = 96;
  const tileH = 84;
  const chartsTop = headerH + tileH + 20;
  const chartsH = rowHeights.reduce((a, b) => a + b + GAP, 0) - GAP;

  measure.font = `italic 11px ${EXPORT_FONT}`;
  const footLines = footnote ? wrapText(measure, footnote, W - PAD * 2) : [];
  const footH = footLines.length ? footLines.length * 15 + 24 : 0;
  const H = chartsTop + chartsH + footH + PAD;

  const canvas = document.createElement('canvas');
  canvas.width = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = PAGE_BG;
  ctx.fillRect(0, 0, W, H);

  // ── Header ────────────────────────────────────────────────────────────
  ctx.fillStyle = INK;
  ctx.font = `700 26px ${EXPORT_FONT}`;
  ctx.fillText(title, PAD, 46);

  const rangeText = `${shortDate(range?.startDate)} – ${shortDate(range?.endDate)}`;
  ctx.font = `15px ${EXPORT_FONT}`;
  ctx.fillStyle = '#4a5568';
  ctx.fillText(presetLabel ? `${rangeText}  ·  ${presetLabel}` : rangeText, PAD, 74);

  ctx.textAlign = 'right';
  ctx.font = `12px ${EXPORT_FONT}`;
  ctx.fillStyle = FAINT;
  ctx.fillText(formatPulledStamp(), W - PAD, 74);
  ctx.textAlign = 'left';

  // ── Summary tiles ─────────────────────────────────────────────────────
  const tileGap = 12;
  const tileW = (W - PAD * 2 - tileGap * (tiles.length - 1)) / tiles.length;
  tiles.forEach((t, i) => {
    const x = PAD + i * (tileW + tileGap);
    ctx.fillStyle = CARD_BG;
    roundRect(ctx, x, headerH, tileW, tileH, 8);
    ctx.fill();
    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = MUTED;
    ctx.font = `600 11px ${EXPORT_FONT}`;
    ctx.fillText(String(t.label).toUpperCase(), x + 16, headerH + 28);
    ctx.fillStyle = INK;
    ctx.font = `700 22px ${EXPORT_FONT}`;
    ctx.fillText(String(t.value), x + 16, headerH + 62);
  });

  // ── Chart cards ───────────────────────────────────────────────────────
  let y = chartsTop;
  rows.forEach((row, ri) => {
    row.forEach((card, ci) => {
      const x = PAD + ci * (CARD_W + GAP);
      ctx.fillStyle = CARD_BG;
      roundRect(ctx, x, y, cardWidth(card.metric), rowHeights[ri], 8);
      ctx.fill();
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = INK;
      ctx.font = `700 15px ${EXPORT_FONT}`;
      ctx.fillText(card.metric.label, x + 20, y + 32);

      card.legendRows.forEach((legendRow, li) => {
        const ly = y + 46 + li * LEGEND_ROW_H + 10;
        ctx.font = `12px ${EXPORT_FONT}`;
        legendRow.forEach(item => {
          const lx = x + 20 + item.x;
          ctx.strokeStyle = item.color;
          ctx.lineWidth = 3;
          ctx.setLineDash(item.dashed ? [4, 3] : []);
          ctx.beginPath();
          ctx.moveTo(lx, ly - 4);
          ctx.lineTo(lx + 12, ly - 4);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = INK;
          ctx.fillText(item.text, lx + 18, ly);
        });
      });

      const chartY = y + 46 + card.legendH;
      const img = images.get(card.metric.key);
      if (img) ctx.drawImage(img, x + 16, chartY, chartWidth(card.metric), card.chartH);

      if (card.noteLines.length) {
        ctx.font = `italic 11px ${EXPORT_FONT}`;
        ctx.fillStyle = MUTED;
        card.noteLines.forEach((line, li) => {
          ctx.fillText(line, x + 20, chartY + card.chartH + 18 + li * 14);
        });
      }
    });
    y += rowHeights[ri] + GAP;
  });

  // ── Footnote ──────────────────────────────────────────────────────────
  if (footLines.length) {
    ctx.font = `italic 11px ${EXPORT_FONT}`;
    ctx.fillStyle = MUTED;
    footLines.forEach((line, i) => ctx.fillText(line, PAD, y + 14 + i * 15));
  }

  const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('could not encode the image');
  return blob;
}
